import { useSyncExternalStore } from 'react';
import { notifications } from '@mantine/notifications';
import { executeBackgroundTask } from '@ayocore/platform/core/client/background-tasks';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Folder } from '../../../schemas/folder.schema';
import { resolveFileUrl } from '../../fileUrl';

/**
 * Координатор уровня вкладки, общий для дерева/списка/плитки. Одна папка может
 * одновременно иметь несколько FolderActionsMenu; локальный useState каждого
 * меню не предотвращал повторный enqueue и несколько window.open по итогу.
 * Серверный coalesce остаётся authoritative между вкладками/после reload.
 */
const activeFolderIds = new Set<number>();
const activeSubscribers = new Set<() => void>();

function subscribeActive(listener: () => void): () => void {
  activeSubscribers.add(listener);
  return () => activeSubscribers.delete(listener);
}

function setFolderActive(folderId: number, active: boolean): void {
  if (active) activeFolderIds.add(folderId);
  else activeFolderIds.delete(folderId);
  for (const listener of activeSubscribers) listener();
}

/** Строковое поле из unknown-результата фоновой задачи. */
function stringField(value: unknown, key: string): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key);
  const field = entry?.[1];
  return typeof field === 'string' ? field : null;
}

function arrayLengthField(value: unknown, key: string): number {
  if (typeof value !== 'object' || value === null) return 0;
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key);
  return Array.isArray(entry?.[1]) ? entry[1].length : 0;
}

/**
 * «Скачать папку» — ZIP поддерева Диска в фоне: ставит задачу ядра
 * 'filemanager::exportFolderArchive' (executeBackgroundTask) и ведёт одно
 * уведомление от «Собираем…» с процентами до ссылки на скачивание. Готовый
 * архив дополнительно приходит durable-уведомлением сервера — переживает
 * обрыв соединения и уход со страницы.
 */
export function useFolderArchiveDownload(folderId: number) {
  const isDownloading = useSyncExternalStore(
    subscribeActive,
    () => activeFolderIds.has(folderId),
    () => false
  );

  const downloadFolderArchive = (folder: Folder) => {
    if (activeFolderIds.has(folder.id)) return;
    setFolderActive(folder.id, true);
    const notificationId = `folder-archive-${folder.id}`;
    notifications.show({
      id: notificationId,
      loading: true,
      title: 'Запрос на архив принят',
      message: folder.name,
      autoClose: false,
      withCloseButton: true,
    });

    const task = executeBackgroundTask('filemanager::exportFolderArchive', {
      folderId: folder.id,
    });
    task.onStatus(status => {
      if (status !== 'queued') return;
      notifications.update({
        id: notificationId,
        loading: true,
        title: 'Архив ожидает в очереди…',
        message: folder.name,
        autoClose: false,
        withCloseButton: true,
      });
    });
    task.onProgress(({ percent, message }) => {
      notifications.update({
        id: notificationId,
        loading: true,
        title: 'Собираем архив…',
        message: message ? `${percent}% · ${message}` : `${folder.name} — ${percent}%`,
        autoClose: false,
        withCloseButton: true,
      });
    });
    task.onDone(result => {
      setFolderActive(folder.id, false);
      const url = stringField(result, 'url');
      const filename = stringField(result, 'filename');
      const skippedCount = arrayLengthField(result, 'skipped');
      notifications.update({
        id: notificationId,
        loading: false,
        color: skippedCount > 0 ? 'yellow' : 'green',
        title: skippedCount > 0 ? 'Архив готов не полностью' : 'Архив готов',
        message: `${filename ?? folder.name}${
          skippedCount > 0 ? ` · пропущено файлов: ${skippedCount}` : ''
        }`,
        autoClose: 6000,
        withCloseButton: true,
      });
      if (url) {
        // Попап-блокер может не пустить (не user gesture) — тогда остаётся
        // durable-уведомление сервера со ссылкой
        window.open(resolveFileUrl(url), '_blank', 'noopener');
      }
    });
    task.onError(message => {
      setFolderActive(folder.id, false);
      notifications.update({
        id: notificationId,
        loading: false,
        color: 'red',
        title: 'Не удалось собрать архив',
        message,
        autoClose: 8000,
        withCloseButton: true,
      });
      logger.error('[drive] Скачивание папки не удалось:', message);
    });
  };

  return { isDownloading, downloadFolderArchive };
}
